import { useTranslation } from 'react-i18next';
import type { FundamentalCatalogItem } from '@/api/types';
import { FundamentalsCatalogGrid } from '@/components/common/FundamentalsCatalog';
import {
  MIN_ANSWERED_SURVEYS,
  MIN_RECEIVED_EVALUATIONS,
  type DashboardGate,
} from '../utils/dashboardGate';

export interface SafeEmptyStateProps {
  /** Catálogo completo de fundamentales, en cualquier orden. */
  catalogData: FundamentalCatalogItem[];
  /** Idioma corto de la UI (es, en, pt…). */
  lang: string;
  /** Avance real hacia el umbral, para no repetir un mensaje genérico. */
  gate: DashboardGate;
}

/** Barra de avance de uno de los dos requisitos del umbral. */
function ProgressRow({ label, current, target }: {
  label: string;
  current: number;
  target: number;
}) {
  const percent = Math.min(100, Math.round((current / target) * 100));
  return (
    <div className="ud-empty-progress-row">
      <div className="ud-empty-progress-meta">
        <span>{label}</span>
        <span className="ud-empty-progress-count">{Math.min(current, target)} / {target}</span>
      </div>
      <div className="ud-empty-progress-track">
        <div className="ud-empty-progress-fill" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

/**
 * Pantalla mostrada mientras la persona no acumula retroalimentación suficiente
 * para calcular su perfil. Presenta el catálogo de fundamentales como avance de
 * lo que verá cuando el dashboard se active.
 */
export function SafeEmptyState({ catalogData, lang, gate }: SafeEmptyStateProps) {
  const { t } = useTranslation();

  return (
    <div className="ud-wrap">
      <div className="ud-empty-state">
        <div className="ud-empty-mascot">
          <img src="/assets/persona-globo.svg" alt="" className="ud-empty-mascot-img" />
        </div>
        <h2>{t('safe.empty.title')}</h2>
        <p>{t('safe.empty.desc')}</p>
        <div className="ud-empty-progress">
          <ProgressRow
            label={t('safe.empty.answeredLabel')}
            current={gate.answeredSurveys}
            target={MIN_ANSWERED_SURVEYS}
          />
          <ProgressRow
            label={t('safe.empty.receivedLabel')}
            current={gate.receivedEvaluations}
            target={MIN_RECEIVED_EVALUATIONS}
          />
        </div>
        <div className="ud-empty-hint">
          <i className="fas fa-info-circle" />
          <span>{t('safe.empty.hint')}</span>
        </div>
        <div className="ud-empty-fundamentals">
          <FundamentalsCatalogGrid catalogData={catalogData} lang={lang} />
        </div>
      </div>
    </div>
  );
}
